class ThemeProduct extends HTMLElement {
  constructor() {
    super();

    this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    this.variantInventory = this.parseJson('[data-product-variant-inventory]', {});
    this.variantQuantityRules = this.parseJson('[data-product-quantity-rules]', {});
    this.variantMedia = this.parseJson('[data-product-variant-media]', {});
    this.mediaGroups = this.parseJson('[data-product-media-groups]', {});
    this.sellingPlanVariants = this.parseJson('[data-product-selling-plans]', {});
    this.optionInputs = this.querySelectorAll('[data-product-option]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.price = this.querySelector('[data-current-price]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.saleBadge = this.querySelector('[data-sale-badge]');
    this.unitPrice = this.querySelector('[data-unit-price]');
    this.variantSku = this.querySelector('[data-variant-sku]');
    this.availability = this.querySelector('[data-product-availability]');
    this.inventoryAvailability = this.querySelector('[data-product-inventory-message]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.productFormShell = this.querySelector('[data-product-form-shell]');
    this.paymentButtonWrapper = this.querySelector('[data-payment-button-wrapper]');
    this.giftCardRecipient = this.querySelector('[data-gift-card-recipient]');
    this.recipientToggle = this.querySelector('[data-recipient-toggle]');
    this.recipientFields = this.querySelectorAll('[data-recipient-field]');
    this.recipientEmail = this.querySelector('[data-recipient-email]');
    this.recipientOffset = this.querySelector('[data-recipient-offset]');
    this.sellingPlans = this.querySelector('[data-selling-plans]');
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    this.purchaseOptionInputs = this.querySelectorAll('[data-purchase-option-input]');
    this.sellingPlanChoices = this.querySelector('[data-selling-plan-choices]');
    this.sellingPlanChoiceInputs = this.querySelectorAll('[data-selling-plan-choice]');
    this.oneTimePurchaseOption = this.querySelector('[data-purchase-option="one-time"]');
    this.oneTimePrice = this.querySelector('[data-one-time-price]');
    this.mediaItems = this.querySelectorAll('[data-product-media]');
    this.mediaThumbnails = this.querySelectorAll('[data-product-media-thumbnail]');
    this.mediaIndicators = this.querySelectorAll('[data-product-media-indicator]');
    this.mediaStage = this.querySelector('[data-product-media-stage]');
    this.imageZoomOpenButtons = this.querySelectorAll('[data-product-image-zoom-open]');
    this.imageZoomCloseButtons = this.querySelectorAll('[data-product-image-zoom-close]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.quantityDecrement = this.querySelector('[data-quantity-decrement]');
    this.quantityIncrement = this.querySelector('[data-quantity-increment]');
    this.quantityRuleMessage = this.querySelector('[data-quantity-rule-message]');
    this.stickyAddToCart = this.querySelector('[data-sticky-add-to-cart]');
    this.stickyAddToCartBar = this.querySelector('[data-sticky-add-to-cart-bar]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.stickyVariant = this.querySelector('[data-sticky-variant]');
    this.pickupContainer = this.querySelector('[data-store-availability-container]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.addLabel = this.dataset.addLabel || 'Add to cart';
    this.addingLabel = this.dataset.addingLabel || 'Adding...';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.unavailableLabel = this.dataset.unavailableLabel || 'Unavailable';
    this.discountTemplate = this.dataset.discountTemplate || '-[[percent]]%';
    this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
    this.pickupAbortController = null;
    this.pickupRequestId = 0;
    this.mediaScrollFrame = null;
    this.mediaSwipeState = null;
    this.readyForVariantAnimation = false;
    this.currentVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0];

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', this.onOptionChange.bind(this));
    });

    this.mediaThumbnails.forEach((button) => {
      button.addEventListener('click', () => this.updateMedia(button.dataset.mediaId));
    });

    this.mediaIndicators.forEach((button) => {
      button.addEventListener('click', () => this.updateMedia(button.dataset.mediaId));
    });

    if (this.mediaStage) {
      this.mediaStage.addEventListener('scroll', this.onMediaStageScroll.bind(this), { passive: true });
      this.mediaStage.addEventListener('pointerdown', this.onMediaStagePointerDown.bind(this));
      this.mediaStage.addEventListener('pointermove', this.onMediaStagePointerMove.bind(this));
      this.mediaStage.addEventListener('pointerup', this.onMediaStagePointerUp.bind(this));
      this.mediaStage.addEventListener('pointercancel', this.onMediaStagePointerCancel.bind(this));
      this.mediaStage.addEventListener('lostpointercapture', this.onMediaStagePointerCancel.bind(this));
      this.mediaStage.addEventListener('touchstart', this.onMediaStageTouchStart.bind(this), { passive: true });
      this.mediaStage.addEventListener('touchmove', this.onMediaStageTouchMove.bind(this), { passive: false });
      this.mediaStage.addEventListener('touchend', this.onMediaStageTouchEnd.bind(this), { passive: true });
      this.mediaStage.addEventListener('touchcancel', this.onMediaStageTouchCancel.bind(this), { passive: true });
      this.mediaStage.addEventListener('dragstart', (event) => event.preventDefault());
    }

    this.purchaseOptionInputs.forEach((input) => {
      input.addEventListener('change', this.onPurchaseOptionChange.bind(this));
    });

    this.sellingPlanChoiceInputs.forEach((input) => {
      input.addEventListener('change', this.onSellingPlanChange.bind(this));
    });

    this.prepareQuantityControl();
    this.prepareStickyAddToCart();
    this.prepareImageZoom();
    this.prepareProductFormLoading();
    this.preparePickupAvailability();
    this.prepareGiftCardRecipient();
    this.updateSellingPlans(this.currentVariant);
    this.updateInventoryAvailability(this.currentVariant);
    this.prepareAccordions();
    this.prepareInfoTabs();
    this.updateOptionAvailability();
    this.updateGalleryForOptions(this.getSelectedOptions());
    this.readyForVariantAnimation = true;
  }

  prepareProductFormLoading() {
    const form = this.variantInput?.form;

    if (!form || !this.addButton) {
      return;
    }

    form.addEventListener('submit', () => {
      this.addButton.value = this.addingLabel;
      this.addButton.setAttribute('aria-busy', 'true');

      if (this.stickyAddToCart) {
        this.stickyAddToCart.disabled = true;
        this.stickyAddToCart.textContent = this.addingLabel;
        this.stickyAddToCart.setAttribute('aria-busy', 'true');
      }
    });
  }

  prepareImageZoom() {
    this.imageZoomOpenButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.getAttribute('aria-controls'));

        if (dialog && typeof dialog.showModal === 'function') {
          this.openMotionDialog(dialog);
          this.syncDialogScrollLock();
        }
      });
    });

    this.imageZoomCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeMotionDialog(button.closest('[data-product-image-zoom-dialog]'));
      });
    });

    this.querySelectorAll('[data-product-image-zoom-dialog]').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        dialog.removeAttribute('data-opening');
        dialog.removeAttribute('data-closing');
        this.syncDialogScrollLock();
      });
      dialog.addEventListener('cancel', (event) => {
        if (this.prefersReducedMotion()) return;
        event.preventDefault();
        this.closeMotionDialog(dialog);
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          this.closeMotionDialog(dialog);
        }
      });
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  motionDuration(name, fallback) {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!value) return fallback;
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return value.endsWith('ms') ? parsed : parsed * 1000;
  }

  scrollBehavior() {
    return this.prefersReducedMotion() ? 'auto' : 'smooth';
  }

  openMotionDialog(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return;

    if (this.prefersReducedMotion()) {
      dialog.showModal();
      return;
    }

    dialog.setAttribute('data-opening', '');
    dialog.showModal();
    requestAnimationFrame(() => dialog.removeAttribute('data-opening'));
  }

  closeMotionDialog(dialog) {
    if (!dialog || !dialog.open) return;

    if (this.prefersReducedMotion()) {
      dialog.close();
      return;
    }

    dialog.setAttribute('data-closing', '');
    window.setTimeout(() => {
      if (dialog.open) dialog.close();
    }, this.motionDuration('--motion-duration-overlay', 320));
  }

  syncDialogScrollLock() {
    const hasOpenScrollLockDialog = Boolean(document.querySelector('dialog[scroll-lock][open]'));
    document.documentElement.classList.toggle('dialog-scroll-locked', hasOpenScrollLockDialog);
  }

  prepareStickyAddToCart() {
    if (!this.stickyAddToCart || !this.stickyAddToCartBar) {
      return;
    }

    this.stickyAddToCart.addEventListener('click', () => {
      if (!this.addButton || this.addButton.disabled) {
        return;
      }

      this.addButton.click();
    });

    this.updateStickyAddToCart(this.currentVariant);

    if (!this.productFormShell || !('IntersectionObserver' in window)) {
      this.stickyAddToCartBar.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.stickyAddToCartBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0.15 }
    );

    observer.observe(this.productFormShell);
  }

  prepareQuantityControl() {
    if (!this.quantityInput) {
      return;
    }

    this.applyQuantityRule(this.currentVariant);

    if (this.quantityDecrement) {
      this.quantityDecrement.addEventListener('click', () => this.stepQuantity(-1));
    }

    if (this.quantityIncrement) {
      this.quantityIncrement.addEventListener('click', () => this.stepQuantity(1));
    }

    this.quantityInput.addEventListener('change', () => this.normalizeQuantityValue());
    this.quantityInput.addEventListener('input', () => this.updateQuantityControls());
    this.updateQuantityControls();
  }

  applyQuantityRule(variant) {
    if (!this.quantityInput || !variant) {
      return;
    }

    const rule = this.getQuantityRule(variant);

    this.quantityInput.min = String(rule.min);
    this.quantityInput.step = String(rule.increment);

    if (rule.max !== null) {
      this.quantityInput.max = String(rule.max);
    } else {
      this.quantityInput.removeAttribute('max');
    }

    this.normalizeQuantityValue();
    this.updateQuantityRuleMessage(rule);
  }

  stepQuantity(direction) {
    if (!this.quantityInput) {
      return;
    }

    const step = this.getQuantityStep();
    const min = this.getQuantityMin();
    const max = this.getQuantityMax();
    const currentValue = Number.parseFloat(this.quantityInput.value);
    const current = Number.isFinite(currentValue) ? currentValue : min;
    const next = this.clampQuantity(current + direction * step);

    this.quantityInput.value = String(next);
    this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateQuantityControls();
  }

  normalizeQuantityValue() {
    if (!this.quantityInput) {
      return;
    }

    const parsedValue = Number.parseFloat(this.quantityInput.value);
    const value = Number.isFinite(parsedValue) ? parsedValue : this.getQuantityMin();

    this.quantityInput.value = String(this.clampQuantity(value));
    this.updateQuantityControls();
  }

  updateQuantityControls() {
    if (!this.quantityInput) {
      return;
    }

    const parsedValue = Number.parseFloat(this.quantityInput.value);
    const value = Number.isFinite(parsedValue) ? parsedValue : this.getQuantityMin();
    const min = this.getQuantityMin();
    const max = this.getQuantityMax();
    const step = this.getQuantityStep();

    if (this.quantityDecrement) {
      this.quantityDecrement.disabled = value - step < min;
    }

    if (this.quantityIncrement) {
      this.quantityIncrement.disabled = max !== null && value + step > max;
    }
  }

  clampQuantity(value) {
    const min = this.getQuantityMin();
    const max = this.getQuantityMax();
    const step = this.getQuantityStep();
    let nextValue = Math.max(min, value);

    if (max !== null) {
      nextValue = Math.min(max, nextValue);
    }

    if (step > 0) {
      const stepsFromMin = Math.round((nextValue - min) / step);
      nextValue = min + stepsFromMin * step;

      if (max !== null && nextValue > max) {
        nextValue = min + Math.floor((max - min) / step) * step;
      }
    }

    nextValue = Math.max(min, nextValue);

    return Number.isInteger(nextValue) ? nextValue : Number(nextValue.toFixed(2));
  }

  getQuantityRule(variant) {
    const rule = this.variantQuantityRules[String(variant.id)] || {};
    const parsedMin = Number.parseFloat(rule.min);
    const parsedIncrement = Number.parseFloat(rule.increment);
    const parsedMax = Number.parseFloat(rule.max);
    const min = Number.isFinite(parsedMin) && parsedMin > 0 ? parsedMin : 1;
    const increment = Number.isFinite(parsedIncrement) && parsedIncrement > 0 ? parsedIncrement : 1;
    const max = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null;

    return { min, increment, max };
  }

  updateQuantityRuleMessage(rule) {
    if (!this.quantityRuleMessage) {
      return;
    }

    const parts = [];

    if (rule.min > 1) {
      parts.push(this.formatQuantityRuleTemplate('minTemplate', rule.min));
    }

    if (rule.increment > 1) {
      parts.push(this.formatQuantityRuleTemplate('incrementTemplate', rule.increment));
    }

    if (rule.max !== null) {
      parts.push(this.formatQuantityRuleTemplate('maxTemplate', rule.max));
    }

    this.quantityRuleMessage.textContent = parts.join(' ');
    this.quantityRuleMessage.hidden = parts.length === 0;
  }

  formatQuantityRuleTemplate(templateKey, value) {
    const template = this.quantityRuleMessage?.dataset[templateKey] || '[[count]]';
    return template.replace('[[count]]', value);
  }

  getQuantityMin() {
    const min = Number.parseFloat(this.quantityInput?.min);
    return Number.isFinite(min) ? min : 1;
  }

  getQuantityMax() {
    const max = Number.parseFloat(this.quantityInput?.max);
    return Number.isFinite(max) ? max : null;
  }

  getQuantityStep() {
    if (!this.quantityInput || this.quantityInput.step === 'any') {
      return 1;
    }

    const step = Number.parseFloat(this.quantityInput.step);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  onOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const selectedVariant = this.variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!selectedVariant) {
      this.setUnavailable();
      this.updateOptionAvailability();
      this.updateGalleryForOptions(selectedOptions);
      return;
    }

    this.updateVariant(selectedVariant);
  }

  prepareGiftCardRecipient() {
    if (!this.giftCardRecipient || !this.recipientToggle) {
      return;
    }

    if (this.recipientOffset) {
      this.recipientOffset.value = String(new Date().getTimezoneOffset());
    }

    const updateRecipientFields = () => {
      const isEnabled = this.recipientToggle.checked;

      this.recipientFields.forEach((field) => {
        field.disabled = !isEnabled;
      });

      if (this.recipientEmail) {
        this.recipientEmail.required = isEnabled;
      }
    };

    this.recipientToggle.addEventListener('change', updateRecipientFields);
    updateRecipientFields();
  }

  getSelectedOptions() {
    const selected = [];

    this.optionInputs.forEach((input) => {
      if (input.matches('select')) {
        selected[Number(input.dataset.optionPosition) - 1] = input.value;
      } else if (input.checked) {
        selected[Number(input.dataset.optionPosition) - 1] = input.value;
      }
    });

    return selected;
  }

  getVariantMediaId(variant) {
    if (!variant) {
      return null;
    }

    if (this.variantMedia && this.variantMedia[variant.id]) {
      return this.variantMedia[variant.id];
    }

    if (variant.featured_media && variant.featured_media.id) {
      return variant.featured_media.id;
    }

    if (variant.featured_image && variant.featured_image.id) {
      return variant.featured_image.id;
    }

    return null;
  }

  updateVariant(variant) {
    this.currentVariant = variant;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this.updatePriceDisplay(variant);

    if (this.unitPrice) {
      if (variant.unit_price && variant.unit_price_measurement) {
        this.unitPrice.hidden = false;
        this.unitPrice.textContent = this.formatUnitPrice(variant);
      } else {
        this.unitPrice.hidden = true;
        this.unitPrice.textContent = '';
      }
    }

    this.updateVariantSku(variant);

    if (this.availability) {
      this.availability.hidden = variant.available;
      this.availability.textContent = variant.available ? '' : this.soldOutLabel;
      this.availability.classList.toggle('product-commerce-badge--sold-out', !variant.available);
      this.availability.classList.remove('product-commerce-badge--unavailable');
    }

    this.updateInventoryAvailability(variant);

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      this.addButton.value = variant.available ? this.addLabel : this.soldOutLabel;
    }

    this.updateStickyAddToCart(variant);
    this.updatePaymentAvailability(variant.available);
    this.applyQuantityRule(variant);
    this.updateSellingPlans(variant);
    this.updateOptionAvailability();
    this.updateGalleryForOptions(variant.options);

    const mediaId = this.getVariantMediaId(variant);

    if (mediaId && this.isMediaVisible(mediaId)) {
      this.updateMedia(mediaId);
    }

    this.updatePickupAvailability(variant.id);
    this.animateVariantContent();
  }

  updatePriceDisplay(pricing) {
    if (!pricing) {
      return;
    }

    if (this.price) {
      this.price.textContent = this.formatMoney(pricing.price);
    }

    if (this.stickyPrice) {
      this.stickyPrice.textContent = this.formatMoney(pricing.price);
    }

    if (this.comparePrice) {
      if (pricing.compare_at_price && pricing.compare_at_price > pricing.price) {
        this.comparePrice.hidden = false;
        this.comparePrice.textContent = this.formatMoney(pricing.compare_at_price);
      } else {
        this.comparePrice.hidden = true;
        this.comparePrice.textContent = '';
      }
    }

    if (this.saleBadge) {
      let discountPercent = 0;

      if (pricing.compare_at_price && pricing.compare_at_price > pricing.price) {
        discountPercent = Math.round(((pricing.compare_at_price - pricing.price) * 100) / pricing.compare_at_price);
      }

      if (discountPercent > 0) {
        this.saleBadge.hidden = false;
        this.saleBadge.textContent = this.discountTemplate.replace('[[percent]]', discountPercent);
      } else {
        this.saleBadge.hidden = true;
        this.saleBadge.textContent = '';
      }
    }
  }

  updateInventoryAvailability(variant) {
    if (!this.inventoryAvailability || !variant) {
      return;
    }

    const inventory = this.variantInventory[String(variant.id)] || {};
    const isTracked = Boolean(inventory.inventory_management);
    const continuesSelling = inventory.inventory_policy === 'continue';
    const quantity = Number.parseInt(inventory.inventory_quantity, 10);
    const threshold = Number.parseInt(this.inventoryAvailability.dataset.lowStockThreshold, 10);
    const lowStockThreshold = Number.isFinite(threshold) ? threshold : 5;

    if (!isTracked || continuesSelling || !inventory.available || !Number.isFinite(quantity)) {
      this.inventoryAvailability.hidden = true;
      this.inventoryAvailability.textContent = '';
      return;
    }

    if (quantity <= lowStockThreshold) {
      const showCount = this.inventoryAvailability.dataset.showInventoryCount === 'true';
      const lowStockCountTemplate = this.inventoryAvailability.dataset.lowStockCountTemplate || 'Only [[count]] left';

      this.inventoryAvailability.textContent = showCount
        ? lowStockCountTemplate.replace('[[count]]', quantity)
        : this.inventoryAvailability.dataset.lowStockLabel || 'Low stock';
    } else {
      this.inventoryAvailability.textContent = this.inventoryAvailability.dataset.inStockLabel || 'In stock';
    }

    this.inventoryAvailability.hidden = false;
  }

  onPurchaseOptionChange(event) {
    if (!event.target.checked) {
      return;
    }

    if (event.target.value === 'one-time') {
      this.selectSellingPlan(null);
      return;
    }

    const firstAvailablePlan = Array.from(this.sellingPlanChoiceInputs).find((input) => !input.disabled);
    const selectedPlan = Array.from(this.sellingPlanChoiceInputs).find((input) => input.checked && !input.disabled);

    this.selectSellingPlan(selectedPlan?.value || firstAvailablePlan?.value || null);
  }

  onSellingPlanChange(event) {
    if (!event.target.checked) {
      return;
    }

    const subscriptionOption = Array.from(this.purchaseOptionInputs).find((input) => input.value === 'subscription');

    if (subscriptionOption) {
      subscriptionOption.checked = true;
    }

    this.selectSellingPlan(event.target.value);
  }

  updateSellingPlans(variant) {
    if (!this.sellingPlans || !variant) {
      return;
    }

    const variantPlans = this.sellingPlanVariants[String(variant.id)] || {
      requires_selling_plan: false,
      allocations: []
    };
    const allocations = Array.isArray(variantPlans.allocations) ? variantPlans.allocations : [];
    const allocationsById = new Map(allocations.map((allocation) => [String(allocation.id), allocation]));
    const requiresSellingPlan = Boolean(variantPlans.requires_selling_plan);
    const currentPlanId = this.sellingPlanInput && !this.sellingPlanInput.disabled ? this.sellingPlanInput.value : '';
    const subscriptionOption = Array.from(this.purchaseOptionInputs).find((input) => input.value === 'subscription');
    const oneTimeOption = Array.from(this.purchaseOptionInputs).find((input) => input.value === 'one-time');

    this.sellingPlans.hidden = allocations.length === 0;

    if (this.oneTimePurchaseOption) {
      this.oneTimePurchaseOption.hidden = requiresSellingPlan;
    }

    if (oneTimeOption) {
      oneTimeOption.disabled = requiresSellingPlan;
    }

    if (subscriptionOption) {
      subscriptionOption.disabled = allocations.length === 0;
    }

    if (this.oneTimePrice) {
      this.oneTimePrice.textContent = this.formatMoney(variant.price);
    }

    this.sellingPlanChoiceInputs.forEach((input) => {
      const allocation = allocationsById.get(input.value);
      const option = input.closest('[data-selling-plan-option]');
      const price = option?.querySelector('[data-selling-plan-price]');

      input.disabled = !allocation;

      if (option) {
        option.hidden = !allocation;
      }

      if (price) {
        price.textContent = allocation ? this.formatMoney(allocation.price) : '';
      }
    });

    this.querySelectorAll('[data-selling-plan-group]').forEach((group) => {
      group.hidden = !Array.from(group.querySelectorAll('[data-selling-plan-choice]')).some((input) => !input.disabled);
    });

    if (allocations.length === 0) {
      this.selectSellingPlan(null);

      if (requiresSellingPlan) {
        if (this.addButton) {
          this.addButton.disabled = true;
          this.addButton.value = this.unavailableLabel;
        }
        this.updatePaymentAvailability(false);
      }
      return;
    }

    const subscriptionWasSelected = subscriptionOption?.checked;
    const currentPlanIsValid = allocationsById.has(currentPlanId);
    let nextPlanId = currentPlanIsValid ? currentPlanId : null;

    if (requiresSellingPlan) {
      nextPlanId = nextPlanId || String(allocations[0].id);
    } else if (subscriptionWasSelected && !currentPlanId) {
      nextPlanId = String(allocations[0].id);
    }

    if (!requiresSellingPlan && (!subscriptionWasSelected || (currentPlanId && !currentPlanIsValid))) {
      nextPlanId = null;
    }

    this.selectSellingPlan(nextPlanId);
  }

  selectSellingPlan(planId) {
    if (!this.currentVariant || !this.sellingPlans) {
      return;
    }

    const variantPlans = this.sellingPlanVariants[String(this.currentVariant.id)] || { allocations: [] };
    const allocation = variantPlans.allocations.find((candidate) => String(candidate.id) === String(planId));
    const subscriptionOption = Array.from(this.purchaseOptionInputs).find((input) => input.value === 'subscription');
    const oneTimeOption = Array.from(this.purchaseOptionInputs).find((input) => input.value === 'one-time');

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = allocation ? String(allocation.id) : '';
      this.sellingPlanInput.disabled = !allocation;
    }

    this.sellingPlanChoiceInputs.forEach((input) => {
      input.checked = Boolean(allocation && input.value === String(allocation.id));
    });

    if (subscriptionOption) {
      subscriptionOption.checked = Boolean(allocation);
    }

    if (oneTimeOption) {
      oneTimeOption.checked = !allocation && !oneTimeOption.disabled;
    }

    if (this.sellingPlanChoices) {
      this.sellingPlanChoices.hidden = !allocation;
    }

    this.updatePriceDisplay(allocation || this.currentVariant);
    this.animateVariantContent();
  }

  updateStickyAddToCart(variant) {
    if (!this.stickyAddToCart || !variant) {
      return;
    }

    this.stickyAddToCart.disabled = !variant.available;
    this.stickyAddToCart.textContent = variant.available ? this.addLabel : this.soldOutLabel;

    if (this.stickyVariant) {
      this.stickyVariant.hidden = this.hasOnlyDefaultVariant;
      this.stickyVariant.textContent = this.hasOnlyDefaultVariant ? '' : variant.title;
    }
  }

  setUnavailable() {
    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.value = this.unavailableLabel;
    }

    if (this.stickyAddToCart) {
      this.stickyAddToCart.disabled = true;
      this.stickyAddToCart.textContent = this.unavailableLabel;
    }

    this.updatePaymentAvailability(false);

    if (this.availability) {
      this.availability.hidden = false;
      this.availability.textContent = this.unavailableLabel;
      this.availability.classList.remove('product-commerce-badge--sold-out');
      this.availability.classList.add('product-commerce-badge--unavailable');
    }

    if (this.inventoryAvailability) {
      this.inventoryAvailability.hidden = true;
      this.inventoryAvailability.textContent = '';
    }

    if (this.variantSku) {
      this.variantSku.hidden = true;
      const skuValue = this.variantSku.querySelector('strong');
      if (skuValue) {
        skuValue.textContent = '';
      }
    }

    if (this.saleBadge) {
      this.saleBadge.hidden = true;
      this.saleBadge.textContent = '';
    }
  }

  updateVariantSku(variant) {
    if (!this.variantSku) {
      return;
    }

    const sku = variant?.sku || '';
    const skuValue = this.variantSku.querySelector('strong');

    this.variantSku.hidden = sku === '';

    if (skuValue) {
      skuValue.textContent = sku;
    }
  }

  updateMedia(mediaId, options = {}) {
    if (!mediaId) {
      return;
    }

    const shouldScroll = options.scroll !== false;

    this.mediaItems.forEach((media) => {
      media.classList.toggle('is-selected', media.dataset.mediaId === String(mediaId));
    });

    this.updateMediaControls(mediaId);

    const selectedMedia = this.querySelector(`[data-product-media][data-media-id="${mediaId}"]`);

    if (selectedMedia && shouldScroll) {
      this.scrollMediaIntoView(selectedMedia);
    }
  }

  scrollMediaIntoView(media) {
    if (!media) {
      return;
    }

    if (this.isMobileMediaGallery() && this.mediaStage?.contains(media)) {
      const targetLeft = media.offsetLeft - this.mediaStage.offsetLeft;

      this.mediaStage.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: this.scrollBehavior()
      });
      return;
    }

    media.scrollIntoView({ behavior: this.scrollBehavior(), block: 'nearest', inline: 'nearest' });
  }

  updateMediaControls(mediaId) {
    [...this.mediaThumbnails, ...this.mediaIndicators].forEach((control) => {
      const isSelected = control.dataset.mediaId === String(mediaId);
      control.classList.toggle('is-selected', isSelected);
      control.setAttribute('aria-current', isSelected ? 'true' : 'false');
    });
  }

  onMediaStageScroll() {
    if (this.mediaScrollFrame) {
      return;
    }

    this.mediaScrollFrame = window.requestAnimationFrame(() => {
      this.mediaScrollFrame = null;
      const closestMedia = this.getClosestVisibleMedia();

      if (closestMedia && !closestMedia.classList.contains('is-selected')) {
        this.updateMedia(closestMedia.dataset.mediaId, { scroll: false });
      }
    });
  }


  onMediaStagePointerDown(event) {
    if (
      !this.isMobileMediaGallery() ||
      !this.mediaStage ||
      this.mediaSwipeState ||
      event.pointerType === 'touch' ||
      event.button > 0 ||
      event.target.closest('button, a, input, select, textarea')
    ) {
      return;
    }

    const selectedMediaId = this.getSelectedMediaId();

    if (!selectedMediaId || this.getVisibleMedia().length < 2) {
      return;
    }

    this.mediaSwipeState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      startScrollLeft: this.mediaStage.scrollLeft,
      selectedMediaId,
      isDragging: false
    };

    this.mediaStage.classList.add('is-swiping');

    if (typeof this.mediaStage.setPointerCapture === 'function') {
      try {
        this.mediaStage.setPointerCapture(event.pointerId);
      } catch (error) {
        // Some embedded browsers can throw here if capture is not available for this pointer.
      }
    }
  }

  onMediaStageTouchStart(event) {
    if (
      !this.isMobileMediaGallery() ||
      !this.mediaStage ||
      this.mediaSwipeState ||
      event.touches.length !== 1 ||
      event.target.closest('button, a, input, select, textarea')
    ) {
      return;
    }

    const selectedMediaId = this.getSelectedMediaId();

    if (!selectedMediaId || this.getVisibleMedia().length < 2) {
      return;
    }

    const touch = event.touches[0];

    this.mediaSwipeState = {
      pointerId: 'touch',
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      startScrollLeft: this.mediaStage.scrollLeft,
      selectedMediaId,
      isDragging: false
    };

    this.mediaStage.classList.add('is-swiping');
  }

  onMediaStagePointerMove(event) {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== event.pointerId || !this.mediaStage) {
      return;
    }

    state.lastX = event.clientX;
    state.lastY = event.clientY;

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!state.isDragging && absX > 8 && absX > absY * 1.15) {
      state.isDragging = true;
    }

    if (!state.isDragging) {
      return;
    }

    event.preventDefault();
    this.mediaStage.scrollLeft = state.startScrollLeft - deltaX;
  }

  onMediaStageTouchMove(event) {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== 'touch' || !this.mediaStage || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];

    state.lastX = touch.clientX;
    state.lastY = touch.clientY;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!state.isDragging && absX > 8 && absX > absY * 1.15) {
      state.isDragging = true;
    }

    if (!state.isDragging) {
      return;
    }

    event.preventDefault();
    this.mediaStage.scrollLeft = state.startScrollLeft - deltaX;
  }

  onMediaStagePointerUp(event) {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== event.pointerId) {
      return;
    }

    this.finishMediaSwipe(event.clientX, event.clientY);
  }

  onMediaStageTouchEnd(event) {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== 'touch') {
      return;
    }

    const touch = event.changedTouches[0];
    this.finishMediaSwipe(touch?.clientX || state.lastX, touch?.clientY || state.lastY);
  }

  onMediaStagePointerCancel(event) {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== event.pointerId) {
      return;
    }

    this.finishMediaSwipe(state.lastX, state.lastY, { cancelled: true });
  }

  onMediaStageTouchCancel() {
    const state = this.mediaSwipeState;

    if (!state || state.pointerId !== 'touch') {
      return;
    }

    this.finishMediaSwipe(state.lastX, state.lastY, { cancelled: true });
  }

  finishMediaSwipe(endX, endY, options = {}) {
    const state = this.mediaSwipeState;

    if (!state) {
      return;
    }

    this.mediaSwipeState = null;

    if (this.mediaStage) {
      this.mediaStage.classList.remove('is-swiping');

      if (typeof state.pointerId === 'number' && typeof this.mediaStage.releasePointerCapture === 'function') {
        try {
          this.mediaStage.releasePointerCapture(state.pointerId);
        } catch (error) {
          // The pointer may already be released by the browser.
        }
      }
    }

    const deltaX = endX - state.startX;
    const deltaY = endY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const closestMedia = this.getClosestVisibleMedia();

    if (options.cancelled) {
      this.updateMedia(closestMedia?.dataset.mediaId || state.selectedMediaId);
      return;
    }

    if (absX < 36 || absX < absY * 1.15) {
      this.updateMedia(closestMedia?.dataset.mediaId || state.selectedMediaId);
      return;
    }

    const nextMediaId = this.getAdjacentMediaIdFrom(state.selectedMediaId, deltaX < 0 ? 1 : -1);

    this.updateMedia(nextMediaId || state.selectedMediaId);
  }

  getSelectedMediaId() {
    const selectedMedia = this.querySelector('[data-product-media].is-selected:not(.is-hidden)');
    return selectedMedia?.dataset.mediaId || null;
  }

  getAdjacentMediaId(direction) {
    return this.getAdjacentMediaIdFrom(this.getSelectedMediaId(), direction);
  }

  getAdjacentMediaIdFrom(mediaId, direction) {
    const visibleMedia = this.getVisibleMedia();

    if (visibleMedia.length < 2) {
      return null;
    }

    const selectedIndex = Math.max(0, visibleMedia.findIndex((media) => media.dataset.mediaId === String(mediaId)));
    const nextIndex = Math.min(Math.max(selectedIndex + direction, 0), visibleMedia.length - 1);

    if (nextIndex === selectedIndex) {
      return null;
    }

    return visibleMedia[nextIndex].dataset.mediaId;
  }

  getVisibleMedia() {
    return Array.from(this.mediaItems).filter((media) => !media.classList.contains('is-hidden'));
  }

  isMobileMediaGallery() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getClosestVisibleMedia() {
    if (!this.mediaStage) {
      return null;
    }

    const stageRect = this.mediaStage.getBoundingClientRect();
    const stageCenter = stageRect.left + stageRect.width / 2;
    let closestMedia = null;
    let closestDistance = Infinity;

    this.mediaItems.forEach((media) => {
      if (media.classList.contains('is-hidden')) {
        return;
      }

      const mediaRect = media.getBoundingClientRect();
      const mediaCenter = mediaRect.left + mediaRect.width / 2;
      const distance = Math.abs(stageCenter - mediaCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestMedia = media;
      }
    });

    return closestMedia;
  }

  updateGalleryForOptions(selectedOptions) {
    let mediaIds = this.getMediaIdsForOptions(selectedOptions);

    if (Array.isArray(mediaIds) && mediaIds.length === 0) {
      const selectedVariant = this.variants.find((variant) => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
      const selectedMediaId = this.getVariantMediaId(selectedVariant || this.currentVariant);

      mediaIds = selectedMediaId ? [selectedMediaId] : null;
    }

    const shouldFilter = Array.isArray(mediaIds) && mediaIds.length > 0;
    const visibleMediaIds = shouldFilter ? new Set(mediaIds.map(String)) : null;

    this.mediaItems.forEach((media) => {
      const isVisible = !shouldFilter || visibleMediaIds.has(String(media.dataset.mediaId));
      media.classList.toggle('is-hidden', !isVisible);
      if (!isVisible) {
        media.classList.remove('is-selected');
      }
    });

    [...this.mediaThumbnails, ...this.mediaIndicators].forEach((control) => {
      const isVisible = !shouldFilter || visibleMediaIds.has(String(control.dataset.mediaId));
      control.classList.toggle('is-hidden', !isVisible);
      control.disabled = !isVisible;
      if (!isVisible) {
        control.classList.remove('is-selected');
        control.setAttribute('aria-current', 'false');
      }
    });

    if (!shouldFilter) {
      const selectedVisibleMedia = this.querySelector('[data-product-media].is-selected:not(.is-hidden)');
      if (!selectedVisibleMedia) {
        const firstMedia = this.mediaItems[0];
        if (firstMedia?.dataset.mediaId) {
          this.updateMedia(firstMedia.dataset.mediaId);
        }
      }
      return;
    }

    const selectedVisibleMedia = this.querySelector('[data-product-media].is-selected:not(.is-hidden)');
    const nextMediaId = selectedVisibleMedia ? selectedVisibleMedia.dataset.mediaId : mediaIds[0];

    if (nextMediaId) {
      this.updateMedia(nextMediaId);
    }
  }

  getMediaIdsForOptions(selectedOptions) {
    let matchingMediaIds = null;

    for (const [position, values] of Object.entries(this.mediaGroups)) {
      const value = selectedOptions[Number(position) - 1];

      if (value && Object.prototype.hasOwnProperty.call(values, value)) {
        const mediaIds = values[value].filter(Boolean).map(String);
        matchingMediaIds = matchingMediaIds === null ? mediaIds : matchingMediaIds.filter((mediaId) => mediaIds.includes(mediaId));
      }
    }

    return matchingMediaIds === null ? null : matchingMediaIds;
  }

  isMediaVisible(mediaId) {
    const media = this.querySelector(`[data-product-media][data-media-id="${mediaId}"]`);
    return Boolean(media && !media.classList.contains('is-hidden'));
  }

  updateOptionAvailability() {
    const selectedOptions = this.getSelectedOptions();

    this.optionInputs.forEach((input) => {
      if (input.matches('select')) {
        Array.from(input.options).forEach((option) => {
          option.disabled = !this.hasExistingVariantForOptionValue(selectedOptions, Number(input.dataset.optionPosition), option.value);
        });
        return;
      }

      input.disabled = !this.hasExistingVariantForOptionValue(selectedOptions, Number(input.dataset.optionPosition), input.value);
    });
  }

  hasExistingVariantForOptionValue(selectedOptions, optionPosition, optionValue) {
    const candidateOptions = [...selectedOptions];
    candidateOptions[optionPosition - 1] = optionValue;

    return this.variants.some((variant) => {
      return variant.options.every((value, index) => value === candidateOptions[index]);
    });
  }

  updatePaymentAvailability(isAvailable) {
    if (this.productFormShell) {
      this.productFormShell.classList.toggle('product-form--sold-out', !isAvailable);
    }

    if (this.paymentButtonWrapper) {
      this.paymentButtonWrapper.hidden = !isAvailable;
    }
  }

  animateVariantContent() {
    if (!this.readyForVariantAnimation) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const elements = [
      this.price?.closest('.product-info__price'),
      this.unitPrice,
      this.variantSku,
      this.availability,
      this.inventoryAvailability,
      this.quantityRuleMessage,
      this.stickyPrice
    ].filter(Boolean);

    elements.forEach((element) => {
      element.classList.remove('is-variant-updated');
      void element.offsetWidth;
      element.classList.add('is-variant-updated');
    });
  }

  updatePickupAvailability(variantId) {
    if (!this.pickupContainer || !variantId) {
      if (this.pickupAbortController) {
        this.pickupAbortController.abort();
        this.pickupAbortController = null;
      }
      return;
    }

    if (this.pickupAbortController) {
      this.pickupAbortController.abort();
    }

    const requestId = this.pickupRequestId + 1;
    this.pickupRequestId = requestId;
    this.pickupAbortController = new AbortController();

    const sectionId = this.pickupContainer.dataset.sectionId || 'pickup-availability';
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const url = `${root}variants/${variantId}/?section_id=${sectionId}`;

    fetch(url, { signal: this.pickupAbortController.signal })
      .then((response) => response.text())
      .then((text) => {
        if (requestId !== this.pickupRequestId) {
          return;
        }

        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');

        this.pickupContainer.innerHTML = section ? section.innerHTML : '';
        this.preparePickupAvailability();
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.pickupRequestId) {
          return;
        }

        this.pickupContainer.innerHTML = '';
      })
      .finally(() => {
        if (requestId === this.pickupRequestId) {
          this.pickupAbortController = null;
        }
      });
  }

  preparePickupAvailability() {
    if (!this.pickupContainer) {
      return;
    }

    const productTitle = this.pickupContainer.dataset.productTitle;
    const hasOnlyDefaultVariant = this.pickupContainer.dataset.hasOnlyDefaultVariant === 'true';
    const title = this.pickupContainer.querySelector('[data-pickup-availability-product-title]');
    const variantTitle = this.pickupContainer.querySelector('[data-pickup-availability-variant-title]');
    const openButton = this.pickupContainer.querySelector('[data-pickup-availability-open]');
    const closeButton = this.pickupContainer.querySelector('[data-pickup-availability-close]');
    const dialog = this.pickupContainer.querySelector('[data-pickup-availability-dialog]');

    if (title && productTitle) {
      title.textContent = productTitle;
    }

    if (variantTitle && hasOnlyDefaultVariant) {
      variantTitle.hidden = true;
    }

    if (!dialog || !openButton || !closeButton) {
      return;
    }

    openButton.addEventListener('click', () => {
      if (typeof dialog.showModal === 'function') {
        this.openMotionDialog(dialog);
        this.syncDialogScrollLock();
      }
    });

    closeButton.addEventListener('click', () => this.closeMotionDialog(dialog));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        this.closeMotionDialog(dialog);
      }
    });

    dialog.addEventListener('cancel', (event) => {
      if (this.prefersReducedMotion()) return;
      event.preventDefault();
      this.closeMotionDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      dialog.removeAttribute('data-opening');
      dialog.removeAttribute('data-closing');
      this.syncDialogScrollLock();
    });
  }

  prepareAccordions() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.querySelectorAll('.product-info__accordion-item').forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.product-info__accordion-content');

      if (!summary || !content || details.dataset.accordionReady === 'true') {
        return;
      }

      details.dataset.accordionReady = 'true';

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.open) {
          this.closeAccordion(details, content);
        } else {
          this.openAccordion(details, content);
        }
      });
    });
  }

  openAccordion(details, content) {
    if (details.dataset.accordionAnimating === 'true') return;

    details.dataset.accordionAnimating = 'true';
    details.open = true;
    content.style.height = '0px';
    content.style.opacity = '0';

    requestAnimationFrame(() => {
      content.style.height = `${content.scrollHeight}px`;
      content.style.opacity = '1';
    });

    this.onAccordionHeightEnd(content, () => {
      content.style.height = '';
      content.style.opacity = '';
      details.dataset.accordionAnimating = 'false';
    });
  }

  closeAccordion(details, content) {
    if (details.dataset.accordionAnimating === 'true') return;

    details.dataset.accordionAnimating = 'true';
    content.style.height = `${content.scrollHeight}px`;
    content.style.opacity = '1';

    requestAnimationFrame(() => {
      content.style.height = '0px';
      content.style.opacity = '0';
    });

    this.onAccordionHeightEnd(content, () => {
      details.open = false;
      content.style.height = '';
      content.style.opacity = '';
      details.dataset.accordionAnimating = 'false';
    });
  }

  onAccordionHeightEnd(content, callback) {
    const onEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onEnd);
      callback();
    };

    content.addEventListener('transitionend', onEnd);
  }


  prepareInfoTabs() {
    const tabButtons = Array.from(this.querySelectorAll('[data-product-info-tab]'));
    const tabPanels = Array.from(this.querySelectorAll('[data-product-info-panel]'));

    if (!tabButtons.length || !tabPanels.length) {
      return;
    }

    const selectTab = (targetId, options = {}) => {
      tabButtons.forEach((button) => {
        const isActive = button.dataset.tabTarget === targetId;
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      tabPanels.forEach((panel) => {
        const isActive = panel.id === targetId;
        panel.hidden = !isActive;

        if (isActive && !this.prefersReducedMotion()) {
          panel.setAttribute('data-entering', '');
          requestAnimationFrame(() => panel.removeAttribute('data-entering'));
        }
      });

      if (options.focus) {
        tabButtons.find((button) => button.dataset.tabTarget === targetId)?.focus();
      }
    };

    this.addEventListener('shopify:block:select', (event) => {
      const selectedPanel = event.target.closest('[data-product-info-panel]');

      if (selectedPanel) {
        selectTab(selectedPanel.id);
      }
    });

    tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => selectTab(button.dataset.tabTarget));

      button.addEventListener('keydown', (event) => {
        let nextIndex = null;

        if (event.key === 'ArrowRight') {
          nextIndex = (index + 1) % tabButtons.length;
        } else if (event.key === 'ArrowLeft') {
          nextIndex = (index - 1 + tabButtons.length) % tabButtons.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabButtons.length - 1;
        }

        if (nextIndex === null) {
          return;
        }

        event.preventDefault();
        selectTab(tabButtons[nextIndex].dataset.tabTarget, { focus: true });
      });
    });

    const initiallySelected = tabButtons.find((button) => button.getAttribute('aria-selected') === 'true') || tabButtons[0];
    selectTab(initiallySelected.dataset.tabTarget);
  }

  formatMoney(cents) {
    const value = Number(cents) / 100;
    const tokenFormatters = {
      amount: () => this.formatMoneyAmount(value, 2, ',', '.'),
      amount_no_decimals: () => this.formatMoneyAmount(value, 0, ',', '.'),
      amount_with_comma_separator: () => this.formatMoneyAmount(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => this.formatMoneyAmount(value, 0, '.', ','),
      amount_with_apostrophe_separator: () => this.formatMoneyAmount(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => this.formatMoneyAmount(value, 0, ' ', '.'),
      amount_with_space_separator: () => this.formatMoneyAmount(value, 2, ' ', ',')
    };

    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      return tokenFormatters[token] ? tokenFormatters[token]() : tokenFormatters.amount();
    });
  }

  formatMoneyAmount(value, decimals, thousandsSeparator, decimalSeparator) {
    const fixed = value.toFixed(decimals);
    const [integerPart, decimalPart] = fixed.split('.');
    const integer = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    if (decimals === 0) {
      return integer;
    }

    return `${integer}${decimalSeparator}${decimalPart}`;
  }

  formatUnitPrice(variant) {
    const measurement = variant.unit_price_measurement;
    const referenceValue = measurement.reference_value === 1 ? '' : measurement.reference_value;

    return `${this.formatMoney(variant.unit_price)} / ${referenceValue}${measurement.reference_unit}`;
  }

  parseJson(selector, fallback) {
    const script = this.querySelector(selector);

    if (!script) {
      return fallback;
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return fallback;
    }
  }
}

if (!customElements.get('theme-product')) {
  customElements.define('theme-product', ThemeProduct);
}
